import { useState } from 'react';
import { trendingPolls, type PollTopic } from '@/mocks/gossip';
import SectionHeading from './SectionHeading';

type Choice = 'A' | 'B';

function formatNum(n: number): string {
  if (n >= 10000) {
    const v = n / 10000;
    return `${v >= 100 ? Math.round(v) : v.toFixed(1)}w`;
  }
  return `${n}`;
}

const toneMap: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-900',
};

function VoteBar({ topic }: { topic: PollTopic }) {
  const total = topic.votesA + topic.votesB;
  const pA = total ? Math.round((topic.votesA / total) * 100) : 50;
  const pB = 100 - pA;
  return (
    <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-background-200">
      <div
        className="flex h-full"
        style={{ width: '100%' }}
        aria-hidden="true"
      >
        <div className="h-full bg-primary-500 transition-all duration-500" style={{ width: `${pA}%` }}></div>
        <div className="h-full bg-accent-500 transition-all duration-500" style={{ width: `${pB}%` }}></div>
      </div>
    </div>
  );
}

function PollCard({ topic }: { topic: PollTopic }) {
  const [my, setMy] = useState<Choice | null>(null);
  const [votesA, setVotesA] = useState(topic.votesA);
  const [votesB, setVotesB] = useState(topic.votesB);
  const total = votesA + votesB;
  const pA = total ? Math.round((votesA / total) * 100) : 50;
  const pB = 100 - pA;

  const vote = (c: Choice) => {
    if (my) return;
    setMy(c);
    if (c === 'A') setVotesA((v) => v + 1);
    else setVotesB((v) => v + 1);
  };

  const pickClass = (c: Choice, label: string, votes: number, pct: number) => {
    const selected = my === c;
    const base =
      'relative flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm transition-all overflow-hidden';
    if (!my) {
      return `${base} cursor-pointer border-background-300 bg-background-50 hover:border-primary-400 hover:bg-primary-50/50`;
    }
    if (selected) {
      return `${base} border-primary-400 bg-primary-50`;
    }
    return `${base} border-background-200 bg-background-50`;
  };

  const color = toneMap[topic.tagColor] ?? toneMap.secondary;

  return (
    <article className="flex flex-col rounded-2xl border border-background-200 bg-background-100 p-4 md:p-5 transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-md font-heading text-base font-black ${
              topic.rank <= 3 ? 'bg-accent-500 text-primary-950' : 'bg-background-300 text-foreground-700'
            }`}
          >
            {topic.rank}
          </span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${color}`}>{topic.tag}</span>
        </div>
        <span className="inline-flex items-center gap-1 text-xs text-foreground-700">
          <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
          {formatNum(topic.heat)} 热度
        </span>
      </div>

      <h3 className="mt-3 font-heading text-base md:text-lg font-bold leading-snug text-foreground-950">
        {topic.title}
      </h3>

      <div className="mt-4 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => vote('A')}
          className={pickClass('A', topic.optionA, votesA, pA)}
          aria-pressed={my === 'A'}
        >
          <span className="font-medium text-foreground-900">{topic.optionA}</span>
          <span className="whitespace-nowrap text-xs text-foreground-700">
            {my ? `${formatNum(votesA)} · ${pA}%` : '站这边'}
          </span>
        </button>
        <button
          type="button"
          onClick={() => vote('B')}
          className={pickClass('B', topic.optionB, votesB, pB)}
          aria-pressed={my === 'B'}
        >
          <span className="font-medium text-foreground-900">{topic.optionB}</span>
          <span className="whitespace-nowrap text-xs text-foreground-700">
            {my ? `${formatNum(votesB)} · ${pB}%` : '站这边'}
          </span>
        </button>
      </div>

      <div className="mt-2">
        <VoteBar topic={{ ...topic, votesA, votesB }} />
        <p className="mt-2 text-right text-xs text-foreground-700">
          {my
            ? `共 ${formatNum(total)} 人参与 · 已站队${my === 'A' ? '前半场' : '后半场'}`
            : `共 ${formatNum(total)} 人参与，投出你的一票`}
        </p>
      </div>
    </article>
  );
}

export default function Trending() {
  return (
    <section id="hot" className="mx-auto w-full max-w-6xl scroll-mt-24 px-4 md:px-8 py-16 md:py-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <SectionHeading
          align="left"
          eyebrow="今日热榜"
          title="全网热议话题"
          accentWord="等你站队"
          desc="每天精选话题发起投票，点击选择你支持的一方，票数实时累加。用投票，让观点被更多人看见。"
        />
        <a
          href="#submit"
          className="whitespace-nowrap inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-md bg-secondary-100 px-4 py-2.5 text-sm font-semibold text-secondary-900 transition-colors hover:bg-secondary-200"
        >
          <i className="ri-discuss-fill" aria-hidden="true"></i>想看新话题，求上线
        </a>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        {trendingPolls.map((t) => (
          <PollCard key={t.id} topic={t} />
        ))}
      </div>
    </section>
  );
}